<script lang="ts">
export default { name: "TextEditorComponent" };
</script>
<script setup lang="ts">
import {
  AllExtensions,
  RequiredExtensions,
  UIRichTextEditor,
} from "@gohighlevel/ghl-text-editor";
import { UIContentWrap } from "@gohighlevel/ghl-ui";
import { History } from "@tiptap/extension-history";
// @ts-ignore - types provided by editor build
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { useAppStore } from "@/store/app";
import { Editor, Extension } from "@tiptap/vue-3";
import { computed, onUnmounted, provide, ref, shallowRef, watch } from "vue";
import FooterStyleMenu from "./FooterStyleMenu.vue";
import { formSurveyRtePrepareSelectAllKey } from "./formSurveyRteToolbar";
import StyleMenu from "./StyleMenu.vue";

interface Props {
  modelValue?: string;
  isSubmitMessage?: boolean;
  size?: string | undefined;
  editorHeight?: string;
  customWidth?: string | undefined;
  contentStyle?: Record<string, string> | undefined;
  mobileInline?: boolean | undefined;
  // Teleport target for embed modals; either a selector or an element
  modalContainer?: string | HTMLElement | null | undefined;
  fallbackFont?: string;
  fallbackFontSize?: number;
  mediaCenterSubCategory?: MediaCenterSubCategory | undefined;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  isSubmitMessage: false,
  size: undefined,
  editorHeight: "84px",
  customWidth: undefined,
  contentStyle: undefined,
  mobileInline: undefined,
  modalContainer: undefined,
  fallbackFont: "inter",
  fallbackFontSize: 16,
  mediaCenterSubCategory: undefined,
});

const store = useAppStore();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const resolvedEditorHeight = computed(() =>
  props.isSubmitMessage ? "120px" : props.editorHeight
);

const mediaCenterSubCategory = computed(
  () =>
    (props.mediaCenterSubCategory ||
      store.mediaCenterSubCategory) as MediaCenterSubCategory
);

const buildExtensions = (): Extension[] => {
  const extensions: Extension[] = [];
  extensions.push(History);
  extensions.push(
    AllExtensions.configure({
      image: { inline: true },
      placeholder: { placeholder: "", showForAllNodes: false },
      divTag: { inline: false, preserveWhitespace: false },
      preTag: { inline: false, preserveWhitespace: false },
      // Allow per-block styles on more nodes
      textAlignment: {
        types: ["heading", "paragraph", "blockquote", "listItem"],
      },
      lineHeight: { types: ["heading", "paragraph", "blockquote", "listItem"] },
      paragraph: { HTMLAttributes: { style: "margin:0px" } },
    })
  );
  extensions.push(RequiredExtensions);
  return extensions;
};

const editor = shallowRef<Editor>(
  new Editor({
    content: props.modelValue || "",
    parseOptions: { preserveWhitespace: "full" },
    editorProps: { attributes: { class: "editor-class-override" } },
    extensions: buildExtensions(),
    onUpdate: ({ editor }) => {
      emit("update:modelValue", editor.getHTML());
    },
  })
);

const richTextEditorRoot = ref<HTMLElement | null>(null);

/**
 * @description Resolves the element receiving a pointer event; `mousedown` targets
 * may be a `Text` node (no `closest`), so we walk to the parent element.
 * @param event - DOM mouse event from capture-phase toolbar handling
 * @returns The nearest HTMLElement for hit-testing, or null
 */
const getMouseEventTargetElement = (event: MouseEvent): HTMLElement | null => {
  const t = event.target;
  if (t instanceof HTMLElement) {
    return t;
  }
  if (t instanceof Text && t.parentElement) {
    return t.parentElement;
  }
  return null;
};

/**
 * @description Focuses the TipTap editor and selects the full document range so the
 * next `ghl-text-editor` toolbar command applies to all content. Pair with
 * `handleToolbarChromeMouseDown` / popover capture; does not inspect DOM skips.
 * @returns {void}
 */
const prepareSelectAllForToolbarCommand = (): void => {
  if (!editor.value) {
    return;
  }
  const { state } = editor.value;
  // Only select all when the cursor is collapsed (no text selected).
  // If the user already selected partial text, respect that selection.
  if (!state.selection.empty) {
    return;
  }
  const docSize = state.doc.content.size;
  editor.value.chain().focus().setTextSelection({ from: 0, to: docSize }).run();
};

/**
 * @description Capture-phase `mousedown` on toolbar chrome: selects the whole document
 * before packaged RTE controls run, except for skips and clicks inside this instance's
 * ProseMirror surface.
 * @param event - The captured mouse event
 * @returns {void}
 */
const handleToolbarChromeMouseDown = (event: MouseEvent): void => {
  const target = getMouseEventTargetElement(event);
  if (!target || !editor.value || !richTextEditorRoot.value) {
    return;
  }
  if (target.closest("[data-skip-select-all]")) {
    return;
  }
  const proseMirror = richTextEditorRoot.value.querySelector(".ProseMirror");
  if (proseMirror?.contains(target)) {
    return;
  }
  prepareSelectAllForToolbarCommand();
};

provide(formSurveyRtePrepareSelectAllKey, prepareSelectAllForToolbarCommand);

watch(
  () => props.modelValue,
  (newValue) => {
    if (!editor.value) {
      return;
    }
    const isSame = editor.value.getHTML() === (newValue || "");
    if (isSame) {
      return;
    }
    editor.value.commands.setContent(newValue || "", false);
  }
);

onUnmounted(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});
</script>

<template>
  <div
    id="richTextEditor"
    ref="richTextEditorRoot"
    class="mb-2 min-w-0 border-gray-400"
  >
    <UIContentWrap
      v-if="editor"
      class="rounded-md border border-gray-300 bg-white"
    >
      <div @mousedown.capture="handleToolbarChromeMouseDown">
        <StyleMenu
          :editor="editor"
          :default-color="props.contentStyle?.color"
          :mobile-inline="props.mobileInline === true"
          :is-submit-message="props.isSubmitMessage === true"
          :modal-container="props.modalContainer"
          :media-center-sub-category="mediaCenterSubCategory"
        />
      </div>
      <div class="min-w-0" :style="props.contentStyle">
        <UIRichTextEditor
          :editor="editor"
          :container-class="`text-black ghl-form-survey-text-editor${
            props.isSubmitMessage ? ' submit-message-editor' : ''
          }`"
        />
      </div>
      <div @mousedown.capture="handleToolbarChromeMouseDown">
        <FooterStyleMenu
          :editor="editor"
          :default-color="props.contentStyle?.color"
          :size="props.size"
          :mobile-inline="props.mobileInline === true"
          :is-submit-message="props.isSubmitMessage === true"
          :fallback-font="props.fallbackFont"
          :fallback-font-size="props.fallbackFontSize"
        />
      </div>
    </UIContentWrap>
  </div>
</template>

<style>
#richTextEditor .ProseMirror {
  overflow-wrap: anywhere;
  hyphens: auto;
  border-radius: 6px;
}
#richTextEditor .ProseMirror:focus-visible {
  outline: none !important;
  border: none !important;
}
#richTextEditor .ProseMirror {
  min-height: 84px;
  overflow: auto;
  height: v-bind("resolvedEditorHeight");
}

.ghl-form-survey-text-editor {
  border: 0 !important;
  padding: 0 !important;
  max-width: v-bind(
    "props.customWidth ? props.customWidth : (props.size === 'mid' ? '66rem' : '33rem')"
  );
}
.ghl-form-survey-text-editor .editor-wrapper {
  padding: 8px;
  max-width: v-bind(
    "props.customWidth ? props.customWidth : (props.size === 'mid' ? '66rem' : '21rem')"
  );
}
.ghl-form-survey-text-editor .ProseMirror {
  font-size: 14px;
  height: 12rem; /* fallback height */
  overflow-y: auto;
  overflow-x: auto;
  resize: vertical;
}
.ghl-form-survey-text-editor .ProseMirror img:not([width]) {
  /* Auto-fit newly inserted images unless an explicit width is set */
  width: 100%;
  height: auto;
  max-width: 100%;
  display: block;
}
.ghl-form-survey-text-editor .ProseMirror p {
  margin: 0;
}
</style>

<style scoped>
:deep(.hl-wrapper-container) {
  padding: 0 !important;
  background-color: #ffffff !important;
}

/* Prevent selection from extending outside the editor */
#richTextEditor {
  contain: paint;
}
</style>
